import React, { useState } from "react";
import styled from "styled-components";
import GlobalPopupContainerCenter from "../globalPopup/GlobalPopupContainerCenter";
import { TextTheme, ColorTheme, BorderRadiusTheme, TallTextTheme, SpacingTheme } from "../globaStyledComponents/globalStyledComponentsStyling";
import useClickOutside from "../../hook/useClickOutside";
import useIsDesktopSize from "../../hook/useIsDesktopSize";
import { SwiperSlide } from "swiper/react";
import ImageGallery from "../gallery/ImageGallery";
import ContinueBtn from "../btn/continueBtn/continueBtn";

interface HotelRoomGroup {
    id: string;
    name: string;
    description?: string;
    images?: string[];
    amenities?: string[];
    room_amenities?: string[];
    maxGuests?: number;
    bedTypes?: string[];
    price?: number;
    rg_ext?: {
        balcony?: number;
        bathroom?: number;
        bedrooms?: number;
        capacity?: number;
        class?: number;
        club?: number;
        family?: number;
        floor?: number;
        quality?: number;
        sex?: number;
        view?: number;
    };
}

interface RoomPopupProps {
    isOpen: boolean;
    onClose: () => void;
    roomData: HotelRoomGroup;
    onReserve?: () => void;
}

const PreviewContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    
    @media (min-width: 768px) {
        flex-direction: row;
    }
`;

const ImageSection = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

// Gallery styles unified via ImageGallery

const RoomPopupImage = styled.img`
    width: 100%;
    max-width: 520px;
    height: 300px;
    object-fit: cover;
    border-radius: ${BorderRadiusTheme.radius_24.borderRadius};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    @media (min-width: 768px) {
        width: 520px;
        height: 520px;
    }
`;

const ContentSection = styled.div`
    flex: 1;
    padding: ${SpacingTheme.spacing_400.padding};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    
    @media (min-width: 768px) {
        padding: ${SpacingTheme.spacing_500.padding};
    }
`;

const RoomPopupTitle = styled.h2`
    ${TextTheme.sans_700_semi}
    color: ${ColorTheme.Black.color};
    margin: 0 0 8px 0;
`;

const RoomPopupType = styled.span`
    ${TextTheme.sans_300_semi}
    color: ${ColorTheme.Grey2.color};
    text-transform: capitalize;
    margin-bottom: 16px;
    display: block;
`;

const RoomPopupDescription = styled.p`
    ${TextTheme.sans_300_semi}
    color: ${ColorTheme.Grey2.color};
    line-height: 1.6;
    margin: 16px 0;
`;

const PriceWrapper = styled.div`
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid ${ColorTheme.Grey4.color};
`;

const RoomPopupPrice = styled.div`
    ${TextTheme.sans_700_semi}
    color: ${ColorTheme.Black.color};
    font-size: 24px;
    
    .total-text {
        ${TextTheme.sans_300_semi}
        color: ${ColorTheme.Black.color};
        font-size: 14px;
        margin-left: 8px;
    }
`;

const AmenitiesSection = styled.div`
    margin: 16px 0;
`;

const AmenitiesTitle = styled.h3`
    ${TextTheme.sans_300_semi}
    color: ${ColorTheme.Black.color};
    margin: 0 0 12px 0;
`;

const AmenitiesList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const AmenityTag = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background-color: #f8f9fa;
    border-radius: 12px;
    font-size: 12px;
    color: ${ColorTheme.Grey2.color};
`;

const RoomDetailsSection = styled.div`
    margin: 16px 0;
`;

const RoomDetailsTitle = styled.h3`
    ${TextTheme.sans_300_semi}
    color: ${ColorTheme.Black.color};
    margin: 0 0 12px 0;
`;

const RoomDetailsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RoomDetailItem = styled.div`
    ${TextTheme.sans_300_semi}
    color: ${ColorTheme.Grey2.color};
    display: flex;
    align-items: center;
    gap: 8px;
`;

const OrderButtonWrapper = styled.div`
    margin-top: 16px;
`;

const MobileButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${SpacingTheme.spacing_300.padding} ${SpacingTheme.spacing_400.padding};
    background: ${ColorTheme.White.color};
    border-top: 1px solid ${ColorTheme.Grey4.color};
    
    @media (min-width: 768px) {
        display: none;
    }
`;

const MobilePriceDisplay = styled.div`
    ${TextTheme.sans_700_semi}
    color: ${ColorTheme.Black.color};
`;

export default function RoomPopup({ isOpen, onClose, roomData, onReserve }: RoomPopupProps) {
    const wrapperRef = useClickOutside(isOpen, () => {
        onClose();
    });
    const isDesktopSize = useIsDesktopSize();

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)} AUD <span class="total-text">total</span>`;
    };

    const getAmenityIcon = (amenity: string) => {
        const amenityIcons: { [key: string]: string } = {
            "air-conditioning": "https://d292awxalydr86.cloudfront.net/Amenities+icons/air-conditioning.svg",
            "balcony": "https://d292awxalydr86.cloudfront.net/Amenities+icons/balcony.svg",
            "barbecue": "https://d292awxalydr86.cloudfront.net/Amenities+icons/barbecue.svg",
            "bath": "https://d292awxalydr86.cloudfront.net/Amenities+icons/bath.svg",
            "bedsheets": "https://d292awxalydr86.cloudfront.net/Amenities+icons/bedsheets.svg",
            "dining-area": "https://d292awxalydr86.cloudfront.net/Amenities+icons/dining-area.svg",
            "fridge": "https://d292awxalydr86.cloudfront.net/Amenities+icons/fridge.svg",
            "heating": "https://d292awxalydr86.cloudfront.net/Amenities+icons/heating.svg",
            "kitchen": "https://d292awxalydr86.cloudfront.net/Amenities+icons/kitchen.svg",
            "kitchen-stuff": "https://d292awxalydr86.cloudfront.net/Amenities+icons/kitchen-stuff.svg",
            "microwave": "https://d292awxalydr86.cloudfront.net/Amenities+icons/microwave.svg",
            "patio": "https://d292awxalydr86.cloudfront.net/Amenities+icons/patio.svg",
            "private-bathroom": "https://d292awxalydr86.cloudfront.net/Amenities+icons/private-bathroom.svg",
            "shower": "https://d292awxalydr86.cloudfront.net/Amenities+icons/shower.svg",
            "tea": "https://d292awxalydr86.cloudfront.net/Amenities+icons/tea.svg",
            "toiletries": "https://d292awxalydr86.cloudfront.net/Amenities+icons/toiletries.svg",
            "towels": "https://d292awxalydr86.cloudfront.net/Amenities+icons/towels.svg",
            "tv": "https://d292awxalydr86.cloudfront.net/Amenities+icons/tv.svg",
            "wardrobe": "https://d292awxalydr86.cloudfront.net/Amenities+icons/wardrobe.svg",
            "washing-machine": "https://d292awxalydr86.cloudfront.net/Amenities+icons/washing-machine.svg",
            "with-view": "https://d292awxalydr86.cloudfront.net/Amenities+icons/with-view.svg",
        };
        return amenityIcons[amenity] || "https://d292awxalydr86.cloudfront.net/Amenities+icons/default.svg";
    };

    const formatAmenityName = (amenity: string) => {
        return amenity
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const handleReserve = () => {
        if (onReserve) {
            onReserve();
        }
        onClose();
    };

    if (!isOpen) return null;

    const roomImages = roomData.images || [];
    const roomAmenities = roomData.room_amenities || [];

    return (
        <GlobalPopupContainerCenter
            ref={wrapperRef}
            closePopup={onClose}
            noContinueBtn={true}
            noCancelBtn={true}
            noBackBtn={isDesktopSize}
            desktopWidth="1200px"
            height="calc(100vh - 12px)"
            desktopHeight="auto"
        >
            <PreviewContent>
                <ImageSection>
                    {roomImages.length > 0 ? (
                        <ImageGallery images={roomImages.map(img => img.replace('{size}', '1024x768'))} altPrefix={roomData.name} />
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <span style={{
                                color: '#999',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}>
                                No images available
                            </span>
                        </div>
                    )}
                </ImageSection>

                <ContentSection>
                    <RoomPopupTitle>{roomData.name}</RoomPopupTitle>
                    
                    {roomData.maxGuests && (
                        <RoomPopupType>
                            Up to {roomData.maxGuests} guests
                        </RoomPopupType>
                    )}

                    {(roomData.rg_ext?.bedrooms || roomData.rg_ext?.bathroom || roomData.rg_ext?.balcony) && (
                        <RoomDetailsSection>
                            <RoomDetailsTitle>Room Details</RoomDetailsTitle>
                            <RoomDetailsList>
                                {roomData.rg_ext?.bedrooms && (
                                    <RoomDetailItem>
                                        <span>🛏️</span>
                                        {roomData.rg_ext.bedrooms} bedroom{roomData.rg_ext.bedrooms > 1 ? 's' : ''}
                                    </RoomDetailItem>
                                )}
                                {roomData.rg_ext?.bathroom && (
                                    <RoomDetailItem>
                                        <span>🚿</span>
                                        {roomData.rg_ext.bathroom} bathroom{roomData.rg_ext.bathroom > 1 ? 's' : ''}
                                    </RoomDetailItem>
                                )}
                                {roomData.rg_ext?.balcony && (
                                    <RoomDetailItem>
                                        <span>🌿</span>
                                        {roomData.rg_ext.balcony} balcony{roomData.rg_ext.balcony > 1 ? 's' : ''}
                                    </RoomDetailItem>
                                )}
                            </RoomDetailsList>
                        </RoomDetailsSection>
                    )}

                    {roomData.bedTypes && roomData.bedTypes.length > 0 && (
                        <RoomDetailsSection>
                            <RoomDetailsTitle>Bed Configuration</RoomDetailsTitle>
                            <RoomDetailsList>
                                <RoomDetailItem>
                                    <span>🛏️</span>
                                    {roomData.bedTypes.join(', ')}
                                </RoomDetailItem>
                            </RoomDetailsList>
                        </RoomDetailsSection>
                    )}

                    {roomData.description && (
                        <RoomPopupDescription>{roomData.description}</RoomPopupDescription>
                    )}

                    {roomAmenities.length > 0 && (
                        <AmenitiesSection>
                            <AmenitiesTitle>Room Amenities</AmenitiesTitle>
                            <AmenitiesList>
                                {roomAmenities.map((amenity, index) => (
                                    <AmenityTag key={index}>
                                        <img 
                                            src={getAmenityIcon(amenity)} 
                                            alt={amenity}
                                            style={{
                                                width: '14px',
                                                height: '14px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                        <span>{formatAmenityName(amenity)}</span>
                                    </AmenityTag>
                                ))}
                            </AmenitiesList>
                        </AmenitiesSection>
                    )}

                    <PriceWrapper>
                        <RoomPopupPrice dangerouslySetInnerHTML={{ __html: formatPrice(roomData.price || 299) }} />
                    </PriceWrapper>

                    <OrderButtonWrapper>
                        <ContinueBtn
                            isLoading={false}
                            disabled={false}
                            background="black"
                            round={true}
                            style={{ width: '100%', height: '48px' }}
                            onClick={handleReserve}
                        >
                            Reserve
                        </ContinueBtn>
                    </OrderButtonWrapper>
                </ContentSection>
            </PreviewContent>
            
            <MobileButtonContainer>
                <MobilePriceDisplay>
                    ${(roomData.price || 299).toFixed(2)} AUD
                </MobilePriceDisplay>
                <ContinueBtn
                    isLoading={false}
                    disabled={false}
                    background="black"
                    round={true}
                    style={{ width: '120px', height: '48px' }}
                    onClick={handleReserve}
                >
                    Reserve
                </ContinueBtn>
            </MobileButtonContainer>
        </GlobalPopupContainerCenter>
    );
} 